import type { ReactNode } from 'react';
import { Icon, type IconName } from './Icon';

export function AppButton({
  children,
  onClick,
  variant = 'filled',
  danger = false,
  type = 'button',
  disabled = false,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'filled' | 'text';
  danger?: boolean;
  type?: 'button' | 'submit';
  /**
   * Недоступная кнопка выглядит недоступной.
   *
   * Раньше несработавшее действие ничем не отличалось от сработавшего: диалог просто
   * молча проглатывал нажатие, а кнопка оставалась синей. Юрист жмёт «Готово», ничего
   * не происходит, и вывод один — сломалось. Поймано на устройстве: «„готово“
   * не жмётся после выбора».
   */
  disabled?: boolean;
}) {
  return (
    <button
      type={type}
      className={`button ${variant === 'text' ? 'button--text' : ''} ${danger ? 'button--danger' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

/** Кнопка действия экрана. Живёт у нижнего края: верхние углы недостижимы одной рукой. */
export function AppFab({
  icon,
  label,
  onClick,
  extended = false,
}: {
  icon: IconName;
  label: string;
  onClick: () => void;
  extended?: boolean;
}) {
  return (
    <button
      type="button"
      className={`fab ${extended ? 'fab--extended' : ''}`}
      aria-label={label}
      onClick={onClick}
    >
      <Icon name={icon} size={24} />
      {extended ? <span>{label}</span> : null}
    </button>
  );
}

/**
 * Чип фильтра. Высота самого чипа 32, но цель касания — 48: вертикальных отступов
 * у блока фильтров нет, поэтому чип центрирован в своей цели, а не растянут.
 */
export function AppChip({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button type="button" className="chip" aria-pressed={selected} onClick={onClick}>
      <span className="chip__body">{label}</span>
    </button>
  );
}

/** Разделитель списка. С ведущей иконкой линия начинается от текста, а не от края. */
/**
 * Переключатель «включено — выключено».
 *
 * Единственный в приложении и потому живёт здесь: голый `<input type="checkbox">`
 * на экране запрещён правилом `rule4` — и не зря, у него нет ни цели касания в 48,
 * ни цветов схемы, ни подписи для чтения с экрана.
 *
 * Кнопка с `role="switch"`, а не поле ввода: так у неё есть подпись, состояние
 * и клавиатура — и ни одного собственного цвета.
 */
export function AppSwitch({
  checked,
  label,
  onChange,
}: {
  checked: boolean;
  /** Что переключаем — для чтения с экрана: на самом переключателе текста нет. */
  label: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      className="switch"
      onClick={() => onChange(!checked)}
    >
      <span className="switch__track">
        <span className="switch__thumb" />
      </span>
    </button>
  );
}

export function AppDivider({ withIcon = false }: { withIcon?: boolean }) {
  return <hr className={`divider ${withIcon ? 'divider--with-icon' : ''}`} />;
}

/**
 * Пустое состояние списка: иконка, объяснение и — по возможности — следующий шаг.
 *
 * Пустое состояние предлагает действие, а не констатирует пустоту: из тупика юрист
 * выходит нажатием, а не догадкой.
 */
export function AppEmptyState({
  icon,
  text,
  action,
}: {
  icon: IconName;
  text: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <Icon name={icon} size={40} className="empty-state__icon" />
      <p className="empty-state__text">{text}</p>
      {action}
    </div>
  );
}

/** Пять типов дела — пять пар «фон плитки / иконка», [D-09](docs/DECISIONS.md). */
export type CaseTypeKey = 'court' | 'claim' | 'consultation' | 'enforcement' | 'other';

const TILE_ICON: Record<CaseTypeKey, IconName> = {
  court: 'gavel',
  claim: 'description',
  consultation: 'forum',
  enforcement: 'account_balance',
  other: 'folder',
};

/**
 * Плитка типа дела в строке списка.
 *
 * Пара «фон / иконка» всегда берётся целиком: цвет иконки проверен на контраст именно
 * к своему фону. Тип продублирован текстом в подзаголовке строки, а иконки отличаются
 * формой — цвет не единственный носитель смысла.
 */
export function CaseTypeTile({ type }: { type: CaseTypeKey }) {
  return (
    <span className={`case-tile case-tile--${type}`}>
      <Icon name={TILE_ICON[type]} size={22} />
    </span>
  );
}
